/**
 * Kanban drag-end handler: finalize column reorder, sidebar drops, and card moves.
 */

import { useCallback } from 'react';
import {
  tryCompleteMissionToSidebarDrop,
  cancelMissionToSidebarDrag,
  isMissionToSidebarDragActive,
} from '../../../../../utils/missionToSidebarBridge';
import { getKanbanDragPointer } from './useDragMoveHandler';
import {
  resolveColumnReorderTarget,
  reorderColumnsIfNeeded,
  resolveCardDropColumn,
  applyCardStatusUpdate,
  getWipMoveCheck,
} from './dragDropUtils';

const useDragEndHandler = ({
  columnIds,
  columns,
  tasksByStatus,
  statuses,
  findColumn,
  getPositionIndex,
  dropIndexRef,
  unlockScrollAndRestore,
  resetDragState,
  onColumnReorder,
  onStatusChange,
  onSetStatusAutoSync,
  onWipLimitBlocked,
}) => {
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    const activeData = active.data?.current;
    const overData = over?.data?.current;
    const dropIndex = dropIndexRef.current;

    unlockScrollAndRestore();
    resetDragState();

    const activeId = active.id;

    if (activeData?.type === 'card' && isMissionToSidebarDragActive()) {
      const pointer = getKanbanDragPointer(event);
      const sidebarDrop = tryCompleteMissionToSidebarDrop(
        pointer?.clientX,
        pointer?.clientY,
      );
      if (sidebarDrop) return;
      cancelMissionToSidebarDrag();
    }

    if (!over) return;

    if (activeData?.type === 'column') {
      const targetColumnId = resolveColumnReorderTarget(over, overData, findColumn, columnIds);
      reorderColumnsIfNeeded(columnIds, activeId, targetColumnId, onColumnReorder);
      return;
    }

    if (activeData?.type !== 'card') return;

    const destColumnId = resolveCardDropColumn(over.id, columnIds, findColumn);
    const sourceColumnId = findColumn(activeId);

    if (destColumnId && sourceColumnId === destColumnId && dropIndex !== null) {
      const sourcePositionIndex = getPositionIndex(activeId, destColumnId);
      if (sourcePositionIndex !== dropIndex) {
        onStatusChange?.(activeId, destColumnId, null, dropIndex);
      }
      return;
    }

    if (!destColumnId || sourceColumnId === destColumnId) return;

    const destColumn = columns.find((c) => c.id === destColumnId);

    if (destColumn?.category !== 'blocked') {
      const wipCheck = getWipMoveCheck(destColumnId, tasksByStatus, statuses, activeId);
      if (!wipCheck.allowed) {
        onWipLimitBlocked?.(wipCheck.reason);
        return;
      }
    }

    if (destColumn?.category === 'blocked') {
      applyCardStatusUpdate(
        activeId,
        destColumnId,
        dropIndex,
        tasksByStatus,
        onStatusChange,
        onSetStatusAutoSync,
      );
    }
  }, [
    columnIds,
    columns,
    findColumn,
    getPositionIndex,
    onColumnReorder,
    onStatusChange,
    onSetStatusAutoSync,
    onWipLimitBlocked,
    statuses,
    tasksByStatus,
    dropIndexRef,
    unlockScrollAndRestore,
    resetDragState,
  ]);

  return handleDragEnd;
};

export default useDragEndHandler;
